import { ReelsContainerHelper } from '@/containers/reels/ReelsContainerHelper'
import { ReelsStripConfig } from '@/containers/reels/ReelsStripConfig'
import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolContainer } from '@/containers/symbols/SymbolContainer'
import { SymbolLayerType } from '@/containers/symbols/SymbolLayerType'
import { SymbolsTypeHelper } from '@/containers/symbols/SymbolsTypeHelper'
import { SymbolType } from '@/containers/symbols/SymbolType'
import Facade from '@/Facade'
import { SymbolsPool } from '@/factories/Factory'
import { IReelsTimingsConfig, NewSymbolData, ReelData, SymbolContainerData, SymbolData } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import gsap from 'gsap'
import { MotionBlurFilter } from 'pixi-filters'
import * as PIXI from 'pixi.js'
import type { TrackEntry as ITrackEntry } from '@esotericsoftware/spine-pixi-v8'

export class ReelContainer {
    // private static readonly BLURRED_SYMBOL_LAYERS: SymbolLayerType[] = [SymbolLayerType.STANDARD_SYMBOLS]

    protected readonly _blurFilter: MotionBlurFilter
    protected readonly _symbolHeight: number
    protected readonly _symbolsCount: number
    protected _stripIndex: number
    protected _speedSettings: IReelsTimingsConfig
    protected _reelsStrip: number[]
    protected _winPositions: number[]
    private rect: PIXI.Graphics

    public reelGrid: number[]
    public originalReelGrid: SymbolData[]
    public symbols: SymbolContainer[]
    public symbolLayers: PIXI.Container[]
    public index: number
    public reelStates = {
        BEFORE_START: 'BEFORE_START',
        STARTING: 'STARTING',
        BEFORE_STOPPING: 'BEFORE_STOPPING',
        STOPPING: 'STOPPING',
        STOPPED: 'STOPPED',
        CASCADING: 'CASCADING',
        SUSPENSE_CASCADING: 'SUSPENSE_CASCADING',
    }
    public reelState: string

    public constructor(data: ReelData) {
        this._blurFilter = new MotionBlurFilter()
        this._blurFilter.velocity = new PIXI.Point(0, Facade.reelsConfig.BLUR_FILTER)
        this._blurFilter.kernelSize = 25
        this.index = data.reelIndex
        this.symbolLayers = []
        this.symbols = []
        this._stripIndex = 0
        this._symbolHeight = ReelsContainerHelper.calculateReelHeight()
        this._symbolsCount = data.reelGrid.length
        this.setState(this.reelStates.BEFORE_START)

        this.selectReelsStrip(data.activeReelStrip)
        this.changeSpinSettings(Facade.isTurboSpin)
        this.createSymbolsLayers()
        this.initSymbols(data)
        this.updateSymbolsAfterStop()
    }

    public selectReelsStrip(reelsStrip: number[]) {
        this._reelsStrip = reelsStrip
        this._stripIndex = this._stripIndex % reelsStrip.length
    }

    public updateHiddenSymbols = (grid: SymbolData[], reelHiddenPositions: number[][]) => {
        /**Update new symbols from server**/
        this._winPositions = reelHiddenPositions.map((position: number[]) => {
            return position[1]
        })
        const updatedSymbolsArray: SymbolContainer[] = []

        this._winPositions.forEach((y: number, index: number, array: number[]) => {
            const symbol: SymbolContainer = this.symbols[y]
            updatedSymbolsArray.push(symbol)
            symbol.y = -this._symbolHeight * (array.length - index) - 33
            symbol.init({
                symbolData: {
                    index: grid[index].id,
                    multiplier: grid[index].multiplier,
                },
                allowUnmasked: false,
            })
            this.changeSymbolLayer(symbol)
        })

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            if (!this._winPositions.includes(index)) {
                updatedSymbolsArray.push(symbol)
            }
        })

        this.symbols = updatedSymbolsArray
    }

    public suspenseSpinStage1 = (): number => {
        this.setState(this.reelStates.SUSPENSE_CASCADING)
        const newNonWinningsSymbols: { symbol: SymbolContainer; index: number }[] = []

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            if (symbol.y !== this._symbolHeight * index && symbol.y >= 0) {
                newNonWinningsSymbols.push({ symbol, index })
            }
        })

        let delay: number = 0
        const duration: number =
            (Facade.anticipationConfig.NON_WINNINGS_SYMBOLS_STOPPING_SPEED /
                Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL) *
            this._winPositions.length
        let finalDelay: number = 0

        newNonWinningsSymbols.forEach((data: { symbol: SymbolContainer; index: number }, _index: number) => {
            const symbol: SymbolContainer = data.symbol
            delay =
                Facade.anticipationConfig.DELAY_PER_NON_WINNINGS_REEL_SYMBOL * (newNonWinningsSymbols.length - _index)

            gsap.to(symbol, {
                delay,
                duration,
                y: data.index * this._symbolHeight + Facade.anticipationConfig.NON_WINNINGS_BOUNCING_SHIFT,
                ease: this._speedSettings.LAST_EASING,
                onComplete: () => {
                    if (Facade.anticipationConfig.NON_WINNINGS_BOUNCING_SHIFT) {
                        const tl = gsap.timeline()
                        tl.to(symbol, {
                            duration: Facade.anticipationConfig.NON_WINNINGS_BOUNCING_BACK_TIME,
                            y: `-=${Facade.anticipationConfig.NON_WINNINGS_BOUNCING_SHIFT}`,
                            ease: this._speedSettings.BOUNCE_EASING,
                            onComplete: () => {
                                this.changeSymbolLayer(symbol, true)
                            },
                            onUpdate: () => {
                                if (tl.progress() >= 0.8) {
                                    this.updateSymbolAfterStop(symbol)
                                }
                            },
                        })
                    }

                    this.playHighSymbolLanding(symbol)
                },
            })

            if (_index === newNonWinningsSymbols.length - 1) {
                finalDelay = delay + duration + Facade.anticipationConfig.NON_WINNINGS_BOUNCING_BACK_TIME
            }
        })

        return finalDelay
    }

    public suspenseSpinStage2 = (isLastCascadeReel: boolean) => {
        const winningsSymbols: SymbolContainer[] = []
        const winningsSymbolsIndexes: number[] = []

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            if (symbol.y !== this._symbolHeight * index && symbol.y < 0) {
                winningsSymbols.push(symbol)
                winningsSymbolsIndexes.push(index)
            }
        })

        winningsSymbols.forEach((symbol: SymbolContainer, index: number) => {
            const duration: number =
                (Facade.anticipationConfig.WINNINGS_SYMBOLS_STOPPING_SPEED /
                    Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL) *
                winningsSymbols.length

            gsap.to(symbol, {
                delay: Facade.anticipationConfig.DELAY_PER_WINNINGS_REEL_SYMBOL * (winningsSymbols.length - index),
                duration,
                y:
                    winningsSymbolsIndexes[index] * this._symbolHeight +
                    Facade.anticipationConfig.WINNINGS_BOUNCING_SHIFT,
                ease: this._speedSettings.LAST_EASING,
                onComplete: () => {
                    if (index === 0) {
                        this.setState(this.reelStates.STOPPED)
                        GameSignals.reelJustTouched.emit(this.index, winningsSymbolsIndexes)
                    }
                    if (Facade.anticipationConfig.WINNINGS_BOUNCING_SHIFT) {
                        const tl = gsap.timeline()
                        tl.to(symbol, {
                            duration: Facade.anticipationConfig.WINNINGS_BOUNCING_BACK_TIME,
                            y: `-=${Facade.anticipationConfig.WINNINGS_BOUNCING_SHIFT}`,
                            ease: this._speedSettings.BOUNCE_EASING,
                            onComplete: () => {
                                this.changeSymbolLayer(symbol, true)
                                if (index === 0 && isLastCascadeReel) {
                                    gsap.to(this, {
                                        duration: 0.5,
                                        onComplete: () => {
                                            GameSignals.completeCascadeSpin.emit()
                                        },
                                    })
                                }
                            },
                            onUpdate: () => {
                                if (tl.progress() >= 0.8) {
                                    this.updateSymbolAfterStop(symbol)
                                }
                            },
                        })
                    }

                    this.playHighSymbolLanding(symbol)
                },
            })
        })

        if (!winningsSymbols.length && isLastCascadeReel) {
            this.setState(this.reelStates.STOPPED)
            GameSignals.reelJustTouched.emit(this.index, winningsSymbolsIndexes)
            gsap.to(this, {
                duration: 0.05,
                onComplete: () => {
                    GameSignals.completeCascadeSpin.emit()
                },
            })
        }
    }

    public cascadeSpin = (isLastCascadeReel: boolean) => {
        this.setState(this.reelStates.CASCADING)
        const winningsSymbolsIndexes: number[] = []
        let movedSymbolsCount: number = 0

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            if (symbol.y !== this._symbolHeight * index && symbol.y < 0) {
                winningsSymbolsIndexes.push(index)
            }
        })

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            const y: number = this._symbolHeight * index
            const isNewSymbol: boolean = symbol.y !== y
            const duration: number =
                (this._speedSettings.STOPPING_SPEED / Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL / 1000) *
                this._winPositions.length

            if (isNewSymbol) {
                movedSymbolsCount++
                gsap.to(symbol, {
                    delay: this._speedSettings.DELAY_PER_REEL_SYMBOL * (this.symbols.length - index),
                    duration,
                    y: index * this._symbolHeight + this._speedSettings.BOUNCING_SHIFT,
                    ease: this._speedSettings.LAST_EASING,
                    onComplete: () => {
                        if (index === 0) {
                            this.setState(this.reelStates.STOPPED)
                            GameSignals.reelJustTouched.emit(this.index, winningsSymbolsIndexes)
                        }
                        if (this._speedSettings.BOUNCING_SHIFT) {
                            const tl = gsap.timeline()
                            tl.to(symbol, {
                                duration: this._speedSettings.BOUNCING_BACK_TIME,
                                y: `-=${this._speedSettings.BOUNCING_SHIFT}`,
                                ease: this._speedSettings.BOUNCE_EASING,
                                onComplete: () => {
                                    this.changeSymbolLayer(symbol, true)
                                    if (index === 0 && isLastCascadeReel) {
                                        gsap.to(this, {
                                            duration: 0.5,
                                            onComplete: () => {
                                                GameSignals.completeCascadeSpin.emit()
                                            },
                                        })
                                    }
                                },
                                onUpdate: () => {
                                    if (tl.progress() >= 0.8) {
                                        this.updateSymbolAfterStop(symbol)
                                    }
                                },
                            })
                        }

                        this.playHighSymbolLanding(symbol)
                    },
                })
            }
        })

        if (!movedSymbolsCount && isLastCascadeReel) {
            this.setState(this.reelStates.STOPPED)
            GameSignals.reelJustTouched.emit(this.index, winningsSymbolsIndexes)
            gsap.to(this, {
                duration: 0.05,
                onComplete: () => {
                    GameSignals.completeCascadeSpin.emit()
                },
            })
        }
    }

    public start() {
        this.setState(this.reelStates.BEFORE_START)
        this.changeSpinSettings(Facade.isTurboSpin)
        this.resetSymbols()
        this.preSpin()
    }

    protected preSpin = () => {
        const timeline = gsap.timeline()

        timeline.to(this.symbols, {
            duration: this._speedSettings.START_BOUNCING_BACK_TIME,
            y: `-=${this._speedSettings.START_BOUNCING_SHIFT}`,
            ease: this._speedSettings.START_BOUNCE_EASING,
            onComplete: () => {
                this.setState(this.reelStates.STARTING)
                this.spinSymbols(this._speedSettings.FIRST_EASING)
            },
        })
    }

    public setData = (reelGrid: number[], originalGrid: SymbolData[], isQuickStop: boolean = false) => {
        this.changeSpinSettings(isQuickStop)
        this.reelGrid = reelGrid
        this.originalReelGrid = originalGrid
    }

    public stoppingReel = (isSuspenseReel: boolean, immediately: boolean) => {
        if (immediately) {
            this.symbols.forEach((symbol: SymbolContainer) => {
                gsap.killTweensOf(symbol)
                symbol.y = this._symbolHeight * Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL + symbol.y + 28
            })
        }

        if (this.reelState === this.reelStates.BEFORE_STOPPING || immediately) {
            this.setState(this.reelStates.STOPPING)
            this.resetSymbolsPositions()
            this.addNewServerSymbols()
            this.stoppingSpinSymbols(isSuspenseReel)
            return
        }

        gsap.to(this, {
            duration: 0.05,
            onComplete: () => {
                this.stoppingReel(false, false)
            },
        })
    }

    protected createSymbol(data: NewSymbolData): SymbolContainer {
        const layerIndex = this.getSymbolLayerIndex(data.symbolData.index, data.allowUnmasked)
        return SymbolsPool.out(this.symbolLayers[layerIndex], data)
    }

    protected createSymbolsLayers() {
        const layersCount = SymbolLayerType.COUNT

        for (let index = 0; index < layersCount; index++) {
            const layer = new PIXI.Container()
            layer.name = `Reel #${this.index + 1}`
            layer.x = Facade.symbolsConfig.WIDTH / 2
            layer.y = this._symbolHeight / 2

            this.symbolLayers.push(layer)
        }
    }

    protected initSymbols(data: ReelData) {
        const { EXTRA_SYMBOLS_TOP, EXTRA_SYMBOLS_BOTTOM } = Facade.reelsConfig

        let symbols = [...data.reelGrid]

        for (let index = 0; index < EXTRA_SYMBOLS_TOP; index++) {
            symbols.unshift(this.getRandomNonSpecialIndex())
        }

        for (let index = 0; index < EXTRA_SYMBOLS_BOTTOM; index++) {
            symbols.push(this.getRandomNonSpecialIndex())
        }

        for (let index = 0; index < symbols.length; index++) {
            const symbolData: SymbolContainerData = this.getDefaultSymbolData(
                symbols[index],
                data.originalGrid[index].multiplier
            )
            const symbol = this.createSymbol({
                symbolData,
                allowUnmasked: true,
            })

            symbol.y = this._symbolHeight * (index - EXTRA_SYMBOLS_TOP)
            symbol.name = '' + symbols[index]
            this.symbols.push(symbol)
        }
    }
    protected onClick(symbol: any) {
        symbol.scale.set(1.3)
    }
    protected addNewServerSymbols() {
        for (let index = this.reelGrid.length - 1; index >= 0; index--) {
            const symbolData = this.getDefaultSymbolData(this.reelGrid[index], this.originalReelGrid[index].multiplier)

            try {
                this.addSymbolOnTop(
                    this.createSymbol({
                        symbolData,
                        allowUnmasked: false,
                    })
                )
            } catch (error) {}
        }
    }

    protected addSymbolOnTop(symbol: SymbolContainer) {
        symbol.y = this.symbols[0].y - this._symbolHeight
        this.changeSymbolLayer(symbol, false, true)
        this.symbols.unshift(symbol)
    }

    protected getDefaultSymbolData(index: number, multiplier: number): SymbolContainerData {
        return {
            index,
            multiplier,
        }
    }

    protected getRandomIndex(): number {
        const randomIndex = ReelsStripConfig.REELS_SYMBOLS_INDEX + this._reelsStrip[this._stripIndex]
        this._stripIndex = (this._stripIndex + 1) % this._reelsStrip.length
        return randomIndex
    }

    protected getRandomNonSpecialIndex(): number {
        const randomIndex = this.getRandomIndex()

        if (randomIndex >= SpecialSymbolType.SCATTER) {
            // replacing special symbols, on adjacent rows they look awful

            const symbolsCount = SymbolType.HP_4 - SymbolType.LP_1
            const randomOffset = Math.floor(Math.random() * symbolsCount)
            return SymbolType.LP_1 + randomOffset
        }

        return randomIndex
    }

    protected getSymbolLayerIndex(index: number, allowUnmasked: boolean = false): number {
        if (SymbolsTypeHelper.isSpecialSymbol(index)) {
            if (allowUnmasked) {
                switch (index) {
                    case SpecialSymbolType.MULTIPLIER:
                        return SymbolLayerType.UNMASKED_SPECIAL_WILD_SYMBOLS
                    case SpecialSymbolType.SCATTER:
                        return SymbolLayerType.UNMASKED_SPECIAL_SCATTER_SYMBOLS
                    default:
                        return SymbolLayerType.UNMASKED_SPECIAL_SYMBOLS
                }
            }

            switch (index) {
                case SpecialSymbolType.SCATTER:
                    return SymbolLayerType.SPECIAL_SCATTER_SYMBOLS
                default:
                    return SymbolLayerType.SPECIAL_SYMBOLS
            }
        }

        if (allowUnmasked) {
            return SymbolLayerType.UNMASKED_STANDARD_SYMBOLS
        } else {
            return SymbolLayerType.STANDARD_SYMBOLS
        }
    }

    protected changeSpinSettings(isQuickSpin: boolean) {
        this._speedSettings = isQuickSpin ? Facade.reelsConfig.QUICK : Facade.reelsConfig.NORMAL
    }

    public changeSymbolLayer(symbol: SymbolContainer, allowUnmasked: boolean = false, forceSpawnBot: boolean = false) {
        const layerIndex = this.getSymbolLayerIndex(symbol.index, allowUnmasked)
        const layer = this.symbolLayers[layerIndex]

        if (forceSpawnBot) {
            layer.addChildAt(symbol, 0)
        } else {
            if (symbol.parent !== layer) {
                layer.addChild(symbol)
            }
        }
    }

    protected setSymbolLayer(symbol: SymbolContainer, layerIndex: number) {
        const layer = this.symbolLayers[layerIndex]

        if (symbol.parent !== layer) {
            layer.addChild(symbol)
        }
    }

    protected spinSymbols(easing: string) {
        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            gsap.to(symbol, {
                duration: this._speedSettings.REGULAR_SPEED / 1000,
                delay: this._speedSettings.DELAY_PER_REEL_SYMBOL * (this.symbols.length - index),
                y: this._symbolHeight * Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL + symbol.y + 28, // +28 to compensate big symbols like Scatter
                ease: easing,
                onComplete: () => {
                    if (index === this.symbols.length - 1) {
                        gsap.to(symbol, {
                            duration: Facade.isTurboSpin ? 0.2 : 0.1,
                            onComplete: () => {
                                this.setState(this.reelStates.BEFORE_STOPPING)
                            },
                        })
                    }
                },
            })
        })
    }

    protected resetSymbolsPositions = () => {
        let y: number = -Facade.reelsConfig.EXTRA_SYMBOLS_TOP * this._symbolHeight - 33 // -33 to compensate big symbols like Bear

        this.symbols.forEach((symbol: SymbolContainer) => {
            symbol.y = y
            y += this._symbolHeight
        })
    }

    protected stoppingSpinSymbols(isSuspenseReel: boolean) {
        const { EXTRA_SYMBOLS_TOP, EXTRA_SYMBOLS_BOTTOM } = Facade.reelsConfig
        const symbolsCount = this._symbolsCount + EXTRA_SYMBOLS_TOP + EXTRA_SYMBOLS_BOTTOM

        while (this.symbols.length > symbolsCount) {
            SymbolsPool.in(this.symbols.pop())
        }

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            const isLastSymbol: boolean = index === 0
            const delay: number =
                (this._speedSettings.DELAY_PER_REEL_SYMBOL + (isSuspenseReel ? 0.25 : 0)) *
                (this.symbols.length - index)

            gsap.to(symbol, {
                delay,
                duration: this._speedSettings.STOPPING_SPEED / 1000,
                y: index * this._symbolHeight + this._speedSettings.BOUNCING_SHIFT,
                ease: this._speedSettings.LAST_EASING,
                onComplete: () => {
                    if (isLastSymbol) {
                        this.setState(this.reelStates.STOPPED)
                    }

                    this.stopSymbol(symbol, isLastSymbol)
                },
            })
        })
    }

    protected stopSymbol = (symbol: SymbolContainer, isLastSymbol: boolean) => {
        if (isLastSymbol) {
            GameSignals.reelJustTouched.emit(this.index, null)
        }

        if (this._speedSettings.BOUNCING_SHIFT) {
            const tl = gsap.timeline()
            tl.to(symbol, {
                duration: this._speedSettings.BOUNCING_BACK_TIME,
                y: `-=${this._speedSettings.BOUNCING_SHIFT}`,
                ease: this._speedSettings.BOUNCE_EASING,
                onComplete: () => {
                    if (isLastSymbol) {
                        GameSignals.reelStopped.emit(this.index)

                        if (this.index === Facade.reelsConfig.TOTAL_REELS - 1) {
                            GameSignals.allReelsStopped.emit()
                        }
                    }
                },
                onUpdate: () => {
                    if (tl.progress() >= 0.8) {
                        this.updateSymbolAfterStop(symbol)
                    }
                },
            })

            const { EXTRA_SYMBOLS_TOP, EXTRA_SYMBOLS_BOTTOM } = Facade.reelsConfig
            const symbolsCount = this._symbolsCount + EXTRA_SYMBOLS_TOP + EXTRA_SYMBOLS_BOTTOM

            for (let i: number = symbolsCount; i < this.symbols.length; i++) {
                this.symbols[i].visible = false
            }
        }

        this.playHighSymbolLanding(symbol)
    }

    protected playHighSymbolLanding(symbol: SymbolContainer) {
        if (
            symbol.index === SymbolType.HP_1 ||
            symbol.index === SymbolType.HP_2 ||
            symbol.index === SymbolType.HP_3 ||
            symbol.index === SymbolType.HP_4
        ) {
            symbol.shadowOut(false)
            symbol.playAnimation('land')
        } else if (
            symbol.index === SymbolType.LP_1 ||
            symbol.index === SymbolType.LP_2 ||
            symbol.index === SymbolType.LP_3 ||
            symbol.index === SymbolType.LP_4 ||
            symbol.index === SymbolType.LP_5
        ) {
            symbol.shadowOut(false)
            symbol.playAnimation('land')
        }
    }

    public playScatterWaiting = (index: number) => {
        if (this.index !== index) return

        for (const symbol of this.symbols) {
            //@ts-ignore
            const currentAnimationName: string = symbol.animation && symbol.animation.state.tracks[0].animation.name

            if (symbol.index === SpecialSymbolType.SCATTER && currentAnimationName !== 'wait') {
                this.changeSymbolLayer(symbol, true)
                symbol.playAnimation('wait', true)
            }
        }
    }

    public playScatterTriggerAnimation = () => {
        for (const symbol of this.symbols) {
            if (symbol.index === SpecialSymbolType.SCATTER) {
                this.setSymbolLayer(symbol, SymbolLayerType.WINNING_SYMBOLS)
                symbol.playAnimation('trigger')
            }
        }
    }

    public stopScattersAnimation = () => {
        for (const symbol of this.symbols) {
            if (symbol.index === SpecialSymbolType.SCATTER && symbol.animation) {
                //TODO Should change this (&& symbol.animation)
                symbol.animation.state.addListener({
                    complete: (entry: ITrackEntry) => {
                        if (!symbol.animation) {
                            // TODO
                            return
                        }
                        symbol.animation.state.clearListeners()
                        symbol.stopAnimation()
                    },
                })
            }
        }
    }

    // protected enableLayersBlur() {
    //     const filters = [this._blurFilter]
    //
    //     for (const index of ReelContainer.BLURRED_SYMBOL_LAYERS) {
    //         this.symbolLayers[index].filters = filters
    //     }
    // }
    //
    // protected disableLayersBlur() {
    //     for (const index of ReelContainer.BLURRED_SYMBOL_LAYERS) {
    //         this.symbolLayers[index].filters = null
    //     }
    // }

    protected updateSymbolsAfterStop() {
        for (const symbol of this.symbols) {
            this.changeSymbolLayer(symbol, true)
        }
    }

    protected updateSymbolAfterStop(symbol: SymbolContainer) {
        this.changeSymbolLayer(symbol, true)
    }

    protected resetSymbols() {
        for (const symbol of this.symbols) {
            this.changeSymbolLayer(symbol)
            symbol.stopAnimation()
        }
    }

    public setState = (value: string) => {
        this.reelState = value
        Facade.reelsStates[this.index] = this.reelState
    }
}
